import { RectangularTag } from '#app/components/RectangularTag/RectangularTag.js';
import { themeV2 } from '#app/theme/themeV2.js';
import { type Location, type UserTypeRouteBase } from '#app/types/router.js';
import { expertPartnerships } from '@contra/expert-partnerships';
import { getRoute } from '@contra/routes';
import { lazy, type ReactNode } from 'react';

const ProTag = lazy(async () => {
  return {
    default: (await import('#app/components/DataVisualizationV2/ProTag.js'))
      .ProTag,
  };
});

export type GetAuthenticatedRouteLabelPayload = {
  label: 'Logo' | ReactNode;
  size?: 'large' | 'small';
  to?: string;
};

type GetAuthenticatedRouteLabelParameters = {
  readonly contextualUsername?: string;
  readonly lastRoute: null | string;
  readonly location: Location;
  readonly pathname: string;
  readonly selectedUserTypeRouteBase?: UserTypeRouteBase;
};
export const regexDiscover = /^\/discover\/(?!discovery).*/;
export const regexCommunity = /\/community/;
const regexIndyAnalyticsViewers =
  /\/independent\/analytics\/viewers\/(portfolio|impressions)/;
const regexIndyDiscovery = /\/independent\/discovery/;
const regexSettings = /\/(help|settings)\/?[\dA-Za-z-]*/;
const regexProjects = /\/projects\/[\dA-Za-z-]+/;
const regexCandidates =
  /\/opportunity\/([\dA-Za-z-]+)\/candidates\/[\dA-Za-z-]+/;
const regexOpportunities = /\/opportunity\/[\dA-Za-z-]+/;
const projectRegex = /\/p\/[\dA-Za-z-]+/;
const servicesRegex = /\/s\/[\dA-Za-z-]+/;

const maybeGetCatchAllLabel = ({
  contextualUsername,
  lastRoute,
  pathname,
  selectedUserTypeRouteBase,
}: GetAuthenticatedRouteLabelParameters): GetAuthenticatedRouteLabelPayload | null => {
  if (regexIndyDiscovery.test(pathname)) {
    return {
      label: (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          Discovery insights <ProTag />
        </div>
      ),
      size: 'large',
      to:
        lastRoute && lastRoute !== getRoute('/independent/discovery')
          ? lastRoute
          : getRoute('/independent/home'),
    };
  }

  if (regexDiscover.test(pathname)) {
    return { label: 'Discover' };
  }

  if (regexCommunity.test(pathname)) {
    return {
      label: (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: '8px',
          }}
        >
          Community
          <RectangularTag
            cursorType="default"
            label="BETA"
            textWeight={600}
            variant="purple"
          />
        </div>
      ),
    };
  }

  for (const expertPartnership of Object.values(expertPartnerships)) {
    if (pathname === expertPartnership.route) {
      if (expertPartnership.name === 'webflow') {
        return { label: `${expertPartnership.displayName} Certified Partners` };
      }

      if (expertPartnership.name === 'flutterflow') {
        return { label: `${expertPartnership.displayName} Expert Coaches` };
      }

      return { label: `${expertPartnership.displayName} Experts` };
    }
  }

  if (projectRegex.test(pathname)) {
    return {
      label: 'All work',
      to: contextualUsername
        ? getRoute('/@username/projects', {
            args: { username: contextualUsername },
          })
        : getRoute('/'),
    };
  }

  if (servicesRegex.test(pathname)) {
    return {
      label: 'All services',
      to: contextualUsername
        ? getRoute('/@username/services', {
            args: { username: contextualUsername },
          })
        : getRoute('/'),
    };
  }

  if (regexProjects.test(pathname)) {
    return {
      label: 'All projects',
      to: selectedUserTypeRouteBase
        ? getRoute('/@userType/projects', {
            args: { userType: selectedUserTypeRouteBase },
          })
        : getRoute('/projects'),
    };
  }

  if (regexCandidates.test(pathname)) {
    const maybeSlug = pathname.match(regexCandidates)?.[1];
    const pageTitle = document.querySelector('title')?.textContent;
    const maybeJobTitle = pageTitle?.split(' | ')[0];

    return {
      label: maybeJobTitle ? (
        <>
          <span style={{ color: themeV2.colorsV2.gray60, fontWeight: 400 }}>
            {maybeJobTitle} /
          </span>
          <span style={{ color: themeV2.colorsV2.black }}>Candidates</span>
        </>
      ) : (
        'Candidates'
      ),
      to: maybeSlug
        ? getRoute('/opportunity/@slug/candidates', {
            args: { slug: maybeSlug },
          })
        : getRoute('/opportunities'),
    };
  }

  if (regexOpportunities.test(pathname)) {
    return {
      label: 'Jobs',
      to: getRoute('/opportunities'),
    };
  }

  if (regexSettings.test(pathname)) {
    return { label: 'Settings' };
  }

  if (regexIndyAnalyticsViewers.test(pathname)) {
    return {
      label: (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          Viewers <ProTag />
        </div>
      ),
      size: 'large',
      to: getRoute('/independent/analytics'),
    };
  }

  return null;
};

export const getAuthenticatedRouteLabel = (
  parameters: GetAuthenticatedRouteLabelParameters,
): GetAuthenticatedRouteLabelPayload => {
  const maybeLabel = maybeGetCatchAllLabel(parameters);
  const urlParameters = new URLSearchParams(parameters.location.search);

  if (maybeLabel) {
    return maybeLabel;
  }

  switch (parameters.pathname) {
    case getRoute('/@internal/admin'):
      return { label: 'Admin' };
    case getRoute('/@userType/projects', {
      args: {
        userType: 'client',
      },
    }):
      return urlParameters.get('drafts') === 'true'
        ? { label: 'Draft Projects' }
        : { label: 'Projects' };
    case getRoute('/@userType/projects', {
      args: {
        userType: 'independent',
      },
    }):
      return urlParameters.get('drafts') === 'true'
        ? { label: 'Draft Projects' }
        : { label: 'Projects' };

    case getRoute('/@userType/wallet', {
      args: { userType: 'client' },
    }):
      return { label: 'Payments' };

    case getRoute('/@userType/wallet', {
      args: { userType: 'independent' },
    }):
      return { label: 'Payments' };
    case getRoute('/client/home'):
      return { label: 'Hiring Dashboard' };

    case getRoute('/client/invoices'):
      return { label: 'Payments' };

    case getRoute('/client/opportunities'):
      return { label: 'Jobs' };
    case getRoute('/company/insights'):
      return { label: 'Insights' };
    case getRoute('/discover/jobs'):
    case getRoute('/discover/jobs/applied'):
      return { label: 'Jobs' };
    case getRoute('/independent/analytics'):
      return { label: 'Analytics' };
    case getRoute('/independent/home'):
      return { label: 'Dashboard' };
    case getRoute('/independent/invoices/new'):
      return { label: 'Payments' };
    case getRoute('/jobs/new'):
      return { label: 'Logo' };
    case getRoute('/messages'):
      return { label: 'Messages' };
    case getRoute('/network'):
      return { label: 'Network' };
    case getRoute('/network/messages'):
      return { label: 'Messages' };
    case getRoute('/projects'):
      return { label: 'Projects' };
    case getRoute('/refer-and-earn'):
      return { label: 'Referrals' };

    default:
      return { label: '' };
  }
};
